import React, { useRef, useState } from 'react';
import { Pause, Play, Volume2, VolumeX, Activity } from 'lucide-react';
import { motion } from 'framer-motion';
import { videoStreamUrl } from '../../lib/api';
import { useLanguage } from '../../i18n';

const SPEEDS = [0.25, 0.5, 1];

export const ResultVideoPlayer = ({ analysisId, hasOverlay, variant, onVariantChange }) => {
  const { t } = useLanguage();
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [speed, setSpeed] = useState(1);
  const [progress, setProgress] = useState(0);
  const [videoDuration, setVideoDuration] = useState(0);

  const togglePlay = () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  };

  const changeSpeed = (s) => {
    setSpeed(s);
    if (videoRef.current) videoRef.current.playbackRate = s;
  };

  const onSeek = (e) => {
    const v = videoRef.current;
    if (!v || !videoDuration) return;
    const val = parseFloat(e.target.value);
    v.currentTime = (val / 100) * videoDuration;
    setProgress(val);
  };

  const changeVariant = (vid) => {
    onVariantChange(vid);
    setPlaying(false);
    setProgress(0);
  };

  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="ss-card p-3">
      {hasOverlay && (
        <div className="flex items-center gap-1.5 mb-2.5 px-0.5" data-testid="result-video-toggle-tabs">
          {[{ id: 'original', label: t('originalVideo') }, { id: 'overlay', label: t('skeletonVideo') }].map((v) => (
            <button
              key={v.id}
              onClick={() => changeVariant(v.id)}
              data-testid={`result-variant-${v.id}`}
              className={`px-3 py-1.5 rounded-[12px] text-xs font-mono border transition-colors ${
                variant === v.id
                  ? 'bg-white/[0.12] border-white/25 text-white'
                  : 'bg-white/[0.04] border-white/10 text-white/50'
              }`}
            >
              {v.id === 'overlay' && <Activity size={11} className="inline mr-1 -mt-px" />}
              {v.label}
            </button>
          ))}
        </div>
      )}
      <div className="relative rounded-[14px] overflow-hidden bg-black aspect-video">
        <video
          key={variant}
          ref={videoRef}
          src={videoStreamUrl(analysisId, variant)}
          playsInline
          muted={muted}
          loop
          className="w-full h-full object-contain"
          data-testid="result-video-player"
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onLoadedMetadata={(e) => {
            setVideoDuration(e.target.duration);
            e.target.playbackRate = speed;
          }}
          onTimeUpdate={(e) => {
            if (e.target.duration) setProgress((e.target.currentTime / e.target.duration) * 100);
          }}
          onClick={togglePlay}
        />
        {!playing && (
          <button onClick={togglePlay} data-testid="result-video-play-overlay" className="absolute inset-0 flex items-center justify-center bg-black/30">
            <span className="w-16 h-16 rounded-[20px] bg-[rgba(34,197,94,0.92)] flex items-center justify-center shadow-[0_0_40px_rgba(34,197,94,0.4)]">
              <Play size={26} className="text-[#06210F] ml-1" />
            </span>
          </button>
        )}
      </div>

      <div className="pt-3 px-1 space-y-2.5">
        <input
          type="range" min="0" max="100" step="0.1" value={progress} onChange={onSeek}
          data-testid="result-video-scrubber"
          className="w-full h-1 rounded-full appearance-none cursor-pointer bg-white/10 accent-[#22C55E]"
        />
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button onClick={togglePlay} data-testid="result-video-play-button" className="w-9 h-9 rounded-[14px] bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-white/[0.1] transition-colors">
              {playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
            </button>
            <button onClick={() => setMuted((m) => !m)} data-testid="result-video-mute-button" className="w-9 h-9 rounded-[14px] bg-white/[0.06] border border-white/10 flex items-center justify-center hover:bg-white/[0.1] transition-colors">
              {muted ? <VolumeX size={15} /> : <Volume2 size={15} />}
            </button>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="font-mono text-[10px] uppercase text-white/40 mr-1">{t('playbackSpeed')}</span>
            {SPEEDS.map((s) => (
              <button
                key={s}
                onClick={() => changeSpeed(s)}
                data-testid={`result-speed-${String(s).replace('.', '-')}`}
                className={`px-2.5 py-1.5 rounded-[12px] font-mono text-xs border transition-colors ${
                  speed === s ? 'bg-white/[0.12] border-white/25 text-white' : 'bg-white/[0.04] border-white/10 text-white/50'
                }`}
              >
                {s}x
              </button>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};
